import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import heartIcon from '../assets/heart.svg';
import heartFilledIcon from '../assets/heart_filled.svg';

const FavoriteDialog = ({ 
  onOpenChange, 
  selections = [], // More general name for items to select
  onConfirm,
  onRemoveAll,
  initialSelectedIds = [], // More general name for selected items
  title,
  subtitle
}) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState(initialSelectedIds);

  const toggleSelection = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleConfirm = () => {
    onConfirm(selectedIds);
    onOpenChange(false);
  };

  return (
    <Dialog.Root defaultOpen={true} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] bg-white rounded-xl flex-col justify-start items-center inline-flex overflow-hidden z-50">
          <div className="self-stretch px-6 pt-8 pb-6 flex-col justify-start items-center gap-2 flex">
            <Dialog.Title className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
              {title || t('favorite.title')}
            </Dialog.Title>
            <Dialog.Description className="self-stretch text-center text-[#666666] text-sm font-normal leading-[21px]">
              {subtitle || t('favorite.subtitle')}
            </Dialog.Description>
            
            <div className="self-stretch pt-6 grid grid-cols-2 gap-3">
              {selections.map((item) => (
                <div 
                  key={item.id}
                  className={`relative h-12 ${item.disabled ? 'opacity-50' : ''}`}
                >
                  {/* Use a border that's always 1px for layout stability */}
                  <div className="absolute inset-0 rounded-md border border-[#dddddd]"></div>
                  
                  {/* Add a visual-only selected border that doesn't affect layout */}
                  {selectedIds.includes(item.id) && (
                    <div className="absolute inset-0 rounded-md border-2 border-[#111111] bg-black/5 pointer-events-none"></div>
                  )}
                  
                  <button
                    onClick={() => !item.disabled && toggleSelection(item.id)}
                    disabled={item.disabled}
                    className="relative z-10 w-full h-full px-3 rounded-md flex justify-between items-center"
                  >
                    <div className="justify-start items-center gap-1 flex">
                      <img 
                        src={selectedIds.includes(item.id) ? heartFilledIcon : heartIcon} 
                        alt="heart" 
                        className="w-4 h-4 mr-1" 
                      />
                      <div className="text-center text-[#111111] text-sm font-medium leading-[21px]">
                        {item.label}
                      </div>
                    </div>
                    <div className="text-center text-[#aaaaaa] text-sm font-normal leading-[21px]">
                      {item.subLabel}
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="self-stretch h-[120px] px-6 pb-6 bg-white flex-col justify-center items-center gap-6 flex">
            <div className="self-stretch justify-start items-center gap-3 inline-flex">
              <Button 
                variant="default" 
                size="l" 
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                {t('favorite.cancel')}
              </Button>
              <Button 
                variant="primary" 
                size="l" 
                className="flex-1"
                onClick={handleConfirm}
                disabled={selectedIds.length === 0}
              >
                {t('favorite.confirm')}
              </Button>
            </div>
            
            {onRemoveAll && (
              <button 
                className="text-center text-[#666666] text-base font-normal leading-normal"
                onClick={() => {
                  onRemoveAll();
                  onOpenChange(false);
                }}
              >
                {t('favorite.remove_all')}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default FavoriteDialog; 